import { AssignationStatus } from "@daytrip/legacy-enums";
import type { Location } from "@daytrip/legacy-models";
import { AssignationForUpcomingTrips } from "@daytrip/legacy-models";
import autobind from "autobind-decorator";
import { action, observable } from "mobx";

import { AssignationWithOrderOperator } from "../../operators/AssignationWithOrderOperator";
import { PageStore } from "../../stores/PageStore";
import { determineAssignationOptions } from "../../utils/assignations/determineOptions";
import { sortAndGroupAssignationWithOrderOperators } from "../../utils/sortAndGroupAssignationWithOrderOperators";

import type { UpcomingTripsPageRouter } from "./UpcomingTripsPageRouter";

@autobind
export class UpcomingTripsPageStore extends PageStore<UpcomingTripsPageRouter, {}> {
    @observable
    public isLoading: boolean = true;

    @observable
    public error: string | undefined = undefined;

    @observable
    public pendingAssignationOperators: AssignationWithOrderOperator[] = [];

    @observable
    public pendingIsLoadingAssignationOperators: boolean = false;

    @observable
    public pendingErrorAssignationOperators: string | undefined = undefined;

    // @observable
    // public haveMorePending: boolean = true;

    @observable
    public acceptedAssignationOperators: AssignationWithOrderOperator[] = [];

    @observable
    public acceptedIsLoadingAssignationOperators: boolean = false;

    @observable
    public acceptedErrorAssignationOperators: string | undefined = undefined;

    // @observable
    // public haveMoreAccepted: boolean = true;

    @observable
    public locations: Array<Location> = [];

    @action
    private setIsLoading(status: AssignationStatus, isLoading: boolean) {
        if (status === AssignationStatus.Pending) {
            this.pendingIsLoadingAssignationOperators = isLoading;
        } else if (status === AssignationStatus.Accepted) {
            this.acceptedIsLoadingAssignationOperators = isLoading;
        }
    }

    @action
    private setError(status: AssignationStatus, error: string | undefined) {
        if (status === AssignationStatus.Pending) {
            this.pendingErrorAssignationOperators = error;
        } else if (status === AssignationStatus.Accepted) {
            this.acceptedErrorAssignationOperators = error;
        }
    }

    @action
    private async fetchAssignations(status: AssignationStatus): Promise<AssignationForUpcomingTrips[]> {
        return this.rpcClient.assignation.retrieveMyAssignationsWithCustomerNameAndBookingReference(
            determineAssignationOptions(status, {}),
        );
    }

    @action
    private async fetchContent(assignationsForUpcomingTrips: AssignationForUpcomingTrips[]) {
        const assignations = assignationsForUpcomingTrips.map(
            (assignationForUpcomingTrip) => assignationForUpcomingTrip.assignation,
        );
        const alreadyFetchedlocationIds = this.locations.map((location) => location._id);

        const locationIds: string[] = [];

        assignations.forEach((assignation) => {
            if (!alreadyFetchedlocationIds.includes(assignation.order.originLocationId)) {
                locationIds.push(assignation.order.originLocationId);
            }
            if (!alreadyFetchedlocationIds.includes(assignation.order.destinationLocationId)) {
                locationIds.push(assignation.order.destinationLocationId);
            }
            locationIds.push(
                ...assignation.order.contentLocations
                    .map((cl) => cl.locationId)
                    .filter((location) => !alreadyFetchedlocationIds.includes(location)),
            );
        });

        const holdLocations = await this.rpcClient.content.retrieveLocations({ ids: locationIds });
        this.locations.push(...holdLocations);
    }

    @action
    private findLocation(locationId: string) {
        return this.locations.find((location) => location._id === locationId);
    }

    @action
    private convertAssignation(assignationForUpcomingTrips: AssignationForUpcomingTrips): AssignationWithOrderOperator {
        const { assignation, customerName, bookingReference } = assignationForUpcomingTrips;
        const { order } = assignation;
        const origin = this.findLocation(order.originLocationId);
        const destination = this.findLocation(order.destinationLocationId);

        if (!origin || !destination) {
            throw new Error(`Missing location data for order ${order._id}`);
        }
        const contentLocationIds = order.contentLocations.map((cl) => cl.locationId);
        const contentLocations = this.locations.filter((location) => contentLocationIds.includes(location._id));

        // if the user is not a company, select him as a driver
        const driverId = !this.authenticationStore.isDriversCompany ? assignation.driverId : undefined;

        const assingationWithOrderOperator = new AssignationWithOrderOperator(
            assignation,
            origin,
            destination,
            contentLocations,
            driverId,
        );
        assingationWithOrderOperator.leadPassengerName = customerName;
        const currentUserId = this.authenticationStore.userJWT?.userId;
        if (currentUserId) {
            assingationWithOrderOperator.userId = currentUserId;
        }
        assingationWithOrderOperator.bookingReference = bookingReference;
        return assingationWithOrderOperator;
    }

    @action
    private async fetchAndConvertAssignations(
        status: AssignationStatus,
    ): Promise<AssignationWithOrderOperator[] | undefined> {
        this.setIsLoading(status, true);
        this.setError(status, undefined);
        try {
            const assingations = await this.fetchAssignations(status);
            await this.fetchContent(assingations);
            return assingations.map((assignation) => this.convertAssignation(assignation));
        } catch (e) {
            console.error(JSON.stringify(e, undefined, 2), e);
            this.setError(status, "Something went wrong!");
        } finally {
            this.setIsLoading(status, false);
        }
        return undefined;
    }

    @action
    public async loadMorePendingAssignations() {
        const assignations = await this.fetchAndConvertAssignations(AssignationStatus.Pending);
        if (assignations) {
            this.pendingAssignationOperators.push(...assignations);
            // this.haveMorePending = assignations?.length === LIMIT;
        }
    }

    @action
    public async loadMoreAcceptedAssignations() {
        const assignations = await this.fetchAndConvertAssignations(AssignationStatus.Accepted);
        if (assignations) {
            this.acceptedAssignationOperators.push(...assignations);
            // this.haveMoreAccepted = assignations?.length === LIMIT;
        }
    }

    @action
    public async reloadAssignations(fetchContent?: boolean) {
        this.isLoading = true;
        this.error = undefined;
        try {
            const [pendingAssignationOperators, acceptedAssignationOperators] = await Promise.all([
                this.fetchAssignations(AssignationStatus.Pending),
                this.fetchAssignations(AssignationStatus.Accepted),
            ]);
            if (fetchContent) {
                await this.fetchContent([...pendingAssignationOperators, ...acceptedAssignationOperators]);
            }
            const groupedPendingAssignationOperators = sortAndGroupAssignationWithOrderOperators(
                pendingAssignationOperators.map((assignation) => this.convertAssignation(assignation)),
            );
            this.pendingAssignationOperators = groupedPendingAssignationOperators;

            const groupedAcceptedAssignationOperators = sortAndGroupAssignationWithOrderOperators(
                acceptedAssignationOperators.map((assignation) => this.convertAssignation(assignation)),
            );
            this.acceptedAssignationOperators = groupedAcceptedAssignationOperators;
            // this.haveMorePending = pendingAssignationOperators.length === LIMIT;
            // this.haveMoreAccepted = acceptedAssignationOperators.length === LIMIT;
        } catch (e) {
            console.error(JSON.stringify(e, undefined, 2), e);
            this.error = "Something went wrong";
        } finally {
            this.isLoading = false;
        }
    }

    @action
    public async onFetchData() {
        await this.reloadAssignations(true);
    }

    public isDataFetched(): this is UpcomingTripsPageStore {
        return !this.isLoading;
    }
}
